import { getAuthItem } from "./authStorage";

/** Permission keys returned by sub-admin API and login step2. */
export const PERMISSION_KEYS = {
  KYC: "kyc",
  DEPOSITS: "deposits",
  WITHDRAWALS: "withdrawals",
  STAKING: "staking",
  USERS: "users",
  LAUNCHPAD: "launchpad",
  SUPPORT: "support",
  REFERRAL: "referral",
  COPY_TRADING: "copy_trading",
};

/** Default landing route per permission (first tab in sidebar order). */
export const PERMISSION_DEFAULT_ROUTES = {
  [PERMISSION_KEYS.USERS]: "/dashboard/homepage",
  [PERMISSION_KEYS.KYC]: "/dashboard/pendingkyc",
  [PERMISSION_KEYS.DEPOSITS]: "/dashboard/fundsDManagement",
  [PERMISSION_KEYS.WITHDRAWALS]: "/dashboard/fundsWithdrawalManagement",
  [PERMISSION_KEYS.STAKING]: "/dashboard/staking-packages",
  [PERMISSION_KEYS.LAUNCHPAD]: "/dashboard/LaunchpadManagement",
  [PERMISSION_KEYS.SUPPORT]: "/dashboard/support",
  [PERMISSION_KEYS.REFERRAL]: "/dashboard/bonus-management",
  [PERMISSION_KEYS.COPY_TRADING]: "/dashboard/copy-trading",
};

const PERMISSION_CHECK_ORDER = [
  PERMISSION_KEYS.USERS,
  PERMISSION_KEYS.KYC,
  PERMISSION_KEYS.DEPOSITS,
  PERMISSION_KEYS.WITHDRAWALS,
  PERMISSION_KEYS.STAKING,
  PERMISSION_KEYS.LAUNCHPAD,
  PERMISSION_KEYS.SUPPORT,
  PERMISSION_KEYS.REFERRAL,
  PERMISSION_KEYS.COPY_TRADING,
];

/** Routes only master admin can access. */
const MASTER_ONLY_PATH_PREFIXES = [
  "/dashboard/listsubadmin",
  "/dashboard/admin-logs",
  "/dashboard/admin-settings",
  "/dashboard/TradingCommision",
  "/dashboard/WithdrawalFees",
  "/dashboard/exchangeWalletManagement",
  "/dashboard/AdminDebitCreditTrans",
  "/dashboard/AnnouncementDetails",
  "/dashboard/notification",
  "/dashboard/send-email",
  "/dashboard/platform-configuration",
  "/dashboard/settings/",
  "/dashboard/p2p-",
  "/dashboard/dispute_",
  "/dashboard/coinListDetails",
  "/dashboard/wallet/",
  "/dashboard/transferTransactions",
];

const normalizePermissionEntry = (entry) => {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") {
    if (typeof entry.key === "string") return entry.key;
    if (entry.value != null) return String(entry.value);
  }
  return null;
};

export const getAdminType = () => {
  const adminType = getAuthItem("adminType");
  if (adminType) return adminType;
  const userType = getAuthItem("userType");
  if (userType === "1" || userType === "admin") return "admin";
  if (userType === "sub_admin") return "sub_admin";
  return "admin";
};

export const isMasterAdmin = () => getAdminType() === "admin";

export const getAdminPermissions = () => {
  try {
    const raw = JSON.parse(getAuthItem("permissions") || "[]");
    if (!Array.isArray(raw)) return [];
    return raw.map(normalizePermissionEntry).filter(Boolean);
  } catch {
    return [];
  }
};

export const hasPermission = (permissionKey) => {
  if (isMasterAdmin()) return true;
  if (!permissionKey) return false;
  return getAdminPermissions().includes(permissionKey);
};

/** Sub-admin with KYC access but no full Users module. */
export const isKycOnlyAdmin = () =>
  !isMasterAdmin() &&
  hasPermission(PERMISSION_KEYS.KYC) &&
  !hasPermission(PERMISSION_KEYS.USERS);

export const getDefaultDashboardRoute = () => {
  if (isMasterAdmin()) return "/dashboard/homepage";
  const permissions = getAdminPermissions();
  for (const key of PERMISSION_CHECK_ORDER) {
    if (permissions.includes(key) && PERMISSION_DEFAULT_ROUTES[key]) {
      return PERMISSION_DEFAULT_ROUTES[key];
    }
  }
  return "/";
};

const pathMatchesPrefix = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const isTraderDetailRoute = (pathname) => pathMatchesPrefix(pathname, "/dashboard/trader");

const isUsersRoute = (pathname) => {
  if (pathMatchesPrefix(pathname, "/dashboard/homepage")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/tradelist")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/unverified-traders")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/blocked-traders")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/addSpotTradingCoin")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/currencymanagement")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/addCurrencyPair")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/currencypair")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/spotMarketTrades")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/OrderBook")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/allOpenOrders")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/futuresPair")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/futuresOpenOrders")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/futuresPositions")) return true;
  if (pathMatchesPrefix(pathname, "/dashboard/futuresTradeHistory")) return true;
  return false;
};

/** Trader detail tabs visible per permission (sub-admin). Master admin sees all. */
const TRADER_TAB_PERMISSIONS = {
  user: [PERMISSION_KEYS.USERS, PERMISSION_KEYS.KYC],
  kyc: [PERMISSION_KEYS.KYC],
  wallet: [PERMISSION_KEYS.USERS, PERMISSION_KEYS.DEPOSITS],
  deposit: [PERMISSION_KEYS.USERS, PERMISSION_KEYS.DEPOSITS, PERMISSION_KEYS.WITHDRAWALS],
  spotOpen: [PERMISSION_KEYS.USERS],
  futureOpen: [PERMISSION_KEYS.USERS],
  futurePositions: [PERMISSION_KEYS.USERS],
  futureHistory: [PERMISSION_KEYS.USERS],
  staking: [PERMISSION_KEYS.STAKING],
  launchpad: [PERMISSION_KEYS.LAUNCHPAD],
  internalWallet: [PERMISSION_KEYS.USERS],
  swapping: [PERMISSION_KEYS.USERS],
  p2p: [PERMISSION_KEYS.USERS],
  p2pAds: [PERMISSION_KEYS.USERS],
  p2pPaymentMethods: [PERMISSION_KEYS.USERS],
  support: [PERMISSION_KEYS.SUPPORT],
  referral: [PERMISSION_KEYS.REFERRAL],
};

export const canAccessTraderTab = (tabKey) => {
  if (isMasterAdmin()) return true;
  const allowed = TRADER_TAB_PERMISSIONS[tabKey];
  if (!allowed) return false;
  return allowed.some((key) => hasPermission(key));
};

export const getVisibleTraderTabSections = (sections) => {
  if (isMasterAdmin()) return sections;
  return sections
    .map((section) => ({
      ...section,
      tabs: section.tabs.filter((tab) => canAccessTraderTab(tab.key)),
    }))
    .filter((section) => section.tabs.length > 0);
};

const isStakingRoute = (pathname) => pathMatchesPrefix(pathname, "/dashboard/staking");

const isSupportRoute = (pathname) =>
  pathMatchesPrefix(pathname, "/dashboard/support") ||
  pathMatchesPrefix(pathname, "/dashboard/UpdateApk");

const isReferralRoute = (pathname) =>
  pathMatchesPrefix(pathname, "/dashboard/bonus-management") ||
  pathMatchesPrefix(pathname, "/dashboard/referral-management") ||
  pathMatchesPrefix(pathname, "/dashboard/upline-downline") ||
  pathMatchesPrefix(pathname, "/dashboard/commission-report") ||
  pathMatchesPrefix(pathname, "/dashboard/referral-release-plans") ||
  pathMatchesPrefix(pathname, "/dashboard/referral/round-releases") ||
  pathMatchesPrefix(pathname, "/dashboard/referral/round-release-report") ||
  pathMatchesPrefix(pathname, "/dashboard/referral/downline-view") ||
  pathMatchesPrefix(pathname, "/dashboard/manage-bonus-funds") ||
  pathMatchesPrefix(pathname, "/dashboard/brokerage");

/** Whether the current admin may open this dashboard path (direct URL included). */
export const canAccessPath = (pathname) => {
  if (!pathname || !pathname.startsWith("/dashboard")) return true;
  if (isMasterAdmin()) return true;

  if (MASTER_ONLY_PATH_PREFIXES.some((prefix) => pathMatchesPrefix(pathname, prefix))) {
    return false;
  }

  if (isUsersRoute(pathname)) return hasPermission(PERMISSION_KEYS.USERS);
  if (isTraderDetailRoute(pathname)) {
    if (pathname.includes("/referral-network")) {
      return hasPermission(PERMISSION_KEYS.USERS) || hasPermission(PERMISSION_KEYS.REFERRAL);
    }
    return hasPermission(PERMISSION_KEYS.USERS) || hasPermission(PERMISSION_KEYS.KYC);
  }
  if (pathMatchesPrefix(pathname, "/dashboard/pendingkyc")) return hasPermission(PERMISSION_KEYS.KYC);
  if (pathMatchesPrefix(pathname, "/dashboard/fundsDManagement") ||
      pathMatchesPrefix(pathname, "/dashboard/deposit-summary")) {
    return hasPermission(PERMISSION_KEYS.DEPOSITS);
  }
  if (pathMatchesPrefix(pathname, "/dashboard/fundsWithdrawalManagement")) {
    return hasPermission(PERMISSION_KEYS.WITHDRAWALS);
  }
  if (isStakingRoute(pathname) || pathMatchesPrefix(pathname, "/dashboard/staking-packages")) {
    return hasPermission(PERMISSION_KEYS.STAKING);
  }
  if (pathMatchesPrefix(pathname, "/dashboard/LaunchpadManagement")) {
    return hasPermission(PERMISSION_KEYS.LAUNCHPAD);
  }
  if (isSupportRoute(pathname)) return hasPermission(PERMISSION_KEYS.SUPPORT);
  if (isReferralRoute(pathname)) return hasPermission(PERMISSION_KEYS.REFERRAL);
  if (pathMatchesPrefix(pathname, "/dashboard/copy-trading")) {
    return hasPermission(PERMISSION_KEYS.COPY_TRADING);
  }

  return false;
};

export const getPermissionLabel = (key, catalog = []) => {
  const match = catalog.find((item) => item.key === key);
  return match?.label || key;
};
